import { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ChildRunApproval, ToolCallDisplay } from '../../../contracts';
import { REQUEST_COMPUTER_LOGIN_TOOL_NAME } from '../../../contracts';
import type { DelegateTraceEntry } from '../../../data';
import {
  computerUseMessages,
  getComputerUseTaskDisplay,
  toUserFacingComputerUseErrorReason,
} from '../../../data';
import { ComputerUsePlanCard } from './ComputerUsePlanCard';
import {
  hasComputerUseActivityEntries,
  type ComputerUsePlanPhase,
} from './ComputerUsePlanCard.utils';
import { ComputerUseStopButton } from './ComputerUseStopButton';
import {
  AwaitingApprovalCard,
  AwaitingApprovalSection,
} from './ComputerUseTaskBlock.approval';
import type {
  ComputerUseTaskBlockHost,
  RenderRecordedWorkflow,
} from './ComputerUseTaskBlock.host';
import { TerminalRecap } from './ComputerUseTaskBlock.progress';
import { ComputerUseRunningBody } from './ComputerUseTaskBlock.running';
import {
  StatusBadge,
  TerminalReason,
  type ComputerUseStatus,
} from './ComputerUseTaskBlock.status';
import { ComputerUseWorkingBlock } from './ComputerUseWorkingBlock';
import { useComputerUseElapsed } from './use-computer-use-elapsed';

export interface ComputerUseTaskBlockProps {
  status: ComputerUseStatus;
  task?: string;
  planSteps?: readonly string[];
  toolCallDisplay?: ToolCallDisplay;
  /**
   * Recorded workflow metadata supplied by skill-named computer-use tools.
   * Immutable recorded guidance with invocation inputs overlaid — not the
   * run's adapted execution plan — so it renders collapsed by default here
   * (running/terminal). Awaiting-approval expands it for review (task-start
   * card and mid-task gates). The app draws it, per placement.
   */
  recordedWorkflow?: RenderRecordedWorkflow;
  /** True while the harness is paused on a generic tool approval gate. */
  awaitingApproval?: boolean;
  /**
   * Resolve the harness approval inline (Approve / Cancel) right at
   * the task card. Returning a Promise lets the inline state machine
   * clear its pending-verdict latch on rejection so the user can
   * retry without remounting (and keep it set on success so a fast
   * second click can't 409 in the BE-OK-but-SSE-not-yet gap). Only
   * wired while ``awaitingApproval``.
   */
  onApprove?: () => void | Promise<void>;
  onDeny?: () => void | Promise<void>;
  /** User-facing reason the approve action is temporarily blocked. */
  approveBlockedMessage?: string;
  /** Step count from the delegate trace; rendered as a small chip. */
  liveStepCount?: number;
  /**
   * Full trace from the delegate hook for the currently-active call,
   * already scoped per `tool_call_id` by the parent. We render the
   * last few entries as a vertical step ladder and expose the full
   * list behind a "Show all" toggle.
   *
   * Optional and only present for the active call — historical blocks
   * render their terminal status without a trace.
   */
  trace?: DelegateTraceEntry[];
  /**
   * Imperative stop handle wired up by the parent. When provided AND
   * the block is running we render an inline Stop button. Skipped for
   * terminal/historical renders.
   */
  onCancel?: () => void;
  /**
   * The user already stopped this (local) desktop task, but the harness
   * timeline still reports it as running until the cancelled tool-result
   * lands. Render a disabled "Stopping…" button instead of an active
   * Stop so the control doesn't read as a fresh, clickable action.
   */
  stopping?: boolean;
  /**
   * Upstream reason for a terminal state (e.g. a rate-limit notice).
   * Errors show it in the card, replaced by a generic
   * fallback when blank, long, or describing screen contents. Success shows
   * it as a note below the card; plain cancellations do not show it.
   */
  terminalReason?: string;
  /**
   * The child sub-agent run executing this task. Used to detect the queued
   * state: while running with no steps yet, if the target's assignment slot
   * is held by a different run, the task is waiting at the resource gate
   * and starts automatically when the holder finishes.
   */
  childAgentRunId?: string;
  /** Pending child-run approval, answered right where the task lives. */
  childRunApproval?: ChildRunApproval | null;
  /**
   * The resolver row above already names this tool; hide the icon tile and
   * family label so the card does not repeat it.
   */
  labelledByRow?: boolean;
  progressRecap?: string;
  /** What the app supplies: the rpa root's legs, and the two cards it binds. */
  host: ComputerUseTaskBlockHost;
}

export const ComputerUseTaskBlock = ({
  status,
  task,
  planSteps,
  toolCallDisplay,
  recordedWorkflow,
  awaitingApproval = false,
  onApprove,
  onDeny,
  approveBlockedMessage,
  liveStepCount,
  trace,
  onCancel,
  stopping = false,
  terminalReason,
  childAgentRunId,
  childRunApproval,
  labelledByRow = false,
  progressRecap,
  host,
}: ComputerUseTaskBlockProps) => {
  const intl = useIntl();
  const [showFullTrace, setShowFullTrace] = useState(false);
  const isRunning = status === 'running';
  const elapsedSeconds = useComputerUseElapsed({
    running: isRunning,
    firstObservedAt: trace?.[0]?.at,
  });

  // Awaiting-approval state at TASK START (no trace accrued yet) gets
  // its own clean replacement card — the body is the ask, not progress,
  // so there's no value showing an empty trace ladder. Mid-task gates
  // (``trace.length > 0``) fall through to the main render below so the
  // existing trace ladder, step counter, and Stop button stay visible
  // beside the inline approval section.
  const hasTrace = (trace?.length ?? 0) > 0;
  if (awaitingApproval && status === 'running' && !hasTrace) {
    return (
      <AwaitingApprovalCard
        task={task}
        planSteps={planSteps}
        toolCallDisplay={toolCallDisplay}
        recordedWorkflow={recordedWorkflow?.({ nested: true, defaultExpanded: true })}
        onApprove={onApprove}
        onDeny={onDeny}
        approveBlockedMessage={approveBlockedMessage}
        renderBlockedWarning={host.renderBlockedWarning}
      />
    );
  }

  const { goal, planSteps: steps } = getComputerUseTaskDisplay(
    task,
    intl,
    toolCallDisplay,
    planSteps
  );
  const reason = terminalReason?.trim();
  const errorReason =
    status === 'error'
      ? toUserFacingComputerUseErrorReason(
          reason ?? '',
          intl.formatMessage(computerUseMessages.errorFallbackReason)
        )
      : undefined;
  const successNote = status === 'success' ? reason : undefined;
  const stoppedRecap = status === 'cancelled' ? progressRecap : undefined;

  const stopButton = isRunning && (onCancel || stopping) && (
    <ComputerUseStopButton onCancel={onCancel} stopping={stopping} />
  );

  const phase: ComputerUsePlanPhase = isRunning
    ? 'running'
    : status === 'success'
      ? 'done'
      : 'stopped';

  return (
    <div
      className="flex w-full flex-col gap-4"
      data-status={status}
      data-awaiting-approval={awaitingApproval ? 'true' : undefined}
    >
      <ComputerUsePlanCard
        title={goal}
        steps={steps}
        phase={phase}
        labelledByRow={labelledByRow}
        // Approvals and failure reasons must stay visible.
        bodyLockedOpen={awaitingApproval || status === 'error'}
        headerAction={
          awaitingApproval || stopButton ? (
            <>
              {awaitingApproval && (
                <StatusBadge
                  status={status}
                  stepCount={liveStepCount}
                  awaitingApproval={awaitingApproval}
                />
              )}
              {stopButton}
            </>
          ) : undefined
        }
      >
        {recordedWorkflow?.({ defaultExpanded: awaitingApproval })}
        {awaitingApproval && (
          <AwaitingApprovalSection
            onApprove={onApprove}
            onDeny={onDeny}
            approveBlockedMessage={approveBlockedMessage}
            renderBlockedWarning={host.renderBlockedWarning}
          />
        )}
        {errorReason && <TerminalReason status="error" reason={errorReason} />}
        {status === 'error' && hasTrace && (
          <TerminalRecap
            status={status}
            trace={trace ?? []}
            showFullTrace={showFullTrace}
            onToggleFullTrace={() => setShowFullTrace((v) => !v)}
          />
        )}
      </ComputerUsePlanCard>

      {isRunning && (
        <ComputerUseRunningBody
          childAgentRunId={childAgentRunId}
          awaitingApproval={awaitingApproval}
          trace={trace}
          elapsedSeconds={elapsedSeconds}
          // The login clarification card embeds its own interactive live
          // view ("Take over"), so the running body's read-only view would
          // just duplicate the screen.
          hideLiveView={childRunApproval?.toolName === REQUEST_COMPUTER_LOGIN_TOOL_NAME}
          runControl={host.runControl}
          renderLiveView={host.renderLiveView}
        />
      )}

      {/* A successful run can still carry guidance, e.g. a partial-result
          note, so it shows here. */}
      {successNote && <TerminalReason status="success" reason={successNote} />}

      {stoppedRecap && <TerminalReason status="cancelled" reason={stoppedRecap} />}

      {!isRunning && status !== 'error' && hasComputerUseActivityEntries(trace) && (
        <ComputerUseWorkingBlock
          trace={trace}
          running={false}
          defaultCollapsed
          elapsedSeconds={elapsedSeconds}
        />
      )}

      {isRunning && childRunApproval && renderChildRunApproval(childRunApproval, host)}
    </div>
  );
};

const renderChildRunApproval = (
  approval: ChildRunApproval,
  { ClarificationBlock, ProtectedActionApproval }: ComputerUseTaskBlockHost
): ReactNode => {
  switch (approval.kind) {
    case 'clarification':
      // For ``login_computer`` this is the sign-in handoff: "Action needed"
      // badge, the resident's screen with Take over, and the resume button.
      return (
        <div data-testid="computer-use-child-clarification">
          <ClarificationBlock approval={approval} textAnswerLocation="card" />
        </div>
      );
    case 'protected_action':
      return <ProtectedActionApproval key={approval.approvalId} approval={approval} />;
    default: {
      const exhaustive: never = approval;
      return exhaustive;
    }
  }
};
